import { useEffect, useState } from 'react';
import FMPPreviewModal from './FMPPreviewModal';

const API_BASE_URL = `${window.location.protocol}//${window.location.hostname}/api`;

function api(path, opts = {}) {
  const token = localStorage.getItem('token');
  return fetch(`${API_BASE_URL}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...opts.headers },
  }).then(r => r.json());
}

const LOGGED_IN_OPTIONS = ['TDC', 'DDC', 'NOC', 'CICO', 'other'];

export default function LocationPanel({ location, onClose, onOpenRack, onEdit, onDelete }) {
  const [racks, setRacks] = useState([]);
  const [fmps, setFmps] = useState([]);
  const [selectedFmp, setSelectedFmp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddRack, setShowAddRack] = useState(false);
  const [newRackLabel, setNewRackLabel] = useState('');
  const [safetyEdit, setSafetyEdit] = useState(null);
  const [savingS, setSavingS] = useState(false);

  useEffect(() => {
    if (!location) return;
    setSafetyEdit({
      high_risk_facility:   location.high_risk_facility   || false,
      nerc_compliance:      location.nerc_compliance      || false,
      directions_911:       location.directions_911       || '',
      nearest_hospital:     location.nearest_hospital     || '',
      logged_in_to_default: location.logged_in_to_default || '',
    });
  }, [location]);

  useEffect(() => {
    if (!location) return;
    setLoading(true);
    Promise.all([
      api(`/racks/location/${location.id}`),
      api(`/fmps?location_id=${location.id}`),
    ])
      .then(([rackData, fmpData]) => {
        setRacks(Array.isArray(rackData) ? rackData : []);
        setFmps(Array.isArray(fmpData) ? fmpData : []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [location]);

  const addRack = async () => {
    if (!newRackLabel.trim()) return;
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_BASE_URL}/racks`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ location_id: location.id, label: newRackLabel, total_units: 42 })
    });
    const rack = await res.json();
    setRacks(prev => [...prev, rack]);
    setNewRackLabel('');
    setShowAddRack(false);
  };

  const saveSafety = async () => {
    setSavingS(true);
    await api(`/locations/${location.id}/safety`, { method: 'PATCH', body: JSON.stringify(safetyEdit) });
    setSavingS(false);
  };

  if (!location) return null;

  const isMobile = window.matchMedia('(pointer: coarse)').matches;
  const statusStyles = {
    draft: { color: '#64748b', bg: '#f1f5f9' },
    no_change: { color: '#64748b', bg: '#f1f5f9' },
    pending_design: { color: '#be185d', bg: '#fce7f3' },
    published: { color: '#15803d', bg: '#dcfce7' },
  };

  return (
    <div style={{
      position: 'fixed',
      // Mobile: bottom sheet sliding up from bottom
      // Desktop: right panel
      ...(isMobile ? {
        bottom: 0, left: 0, right: 0,
        width: '100%', height: '70vh',
        borderRadius: '16px 16px 0 0',
        boxShadow: '0 -4px 24px rgba(0,0,0,0.2)',
      } : {
        top: 0, right: 0,
        width: 320, height: '100vh',
        boxShadow: '-4px 0 16px rgba(0,0,0,0.15)',
      }),
      backgroundColor: 'white', zIndex: 1500,
      display: 'flex', flexDirection: 'column', fontFamily: 'sans-serif',
    }}>
      
      {/* Header */}
      <div style={{ padding: '16px', backgroundColor: '#2d3748', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontWeight: 'bold', fontSize: 16 }}>{location.name}</div>
          <div style={{ fontSize: 12, opacity: 0.7, marginTop: 2 }}>{location.type}</div>
        </div>
        <span onClick={onClose} style={{ cursor: 'pointer', fontSize: 20 }}>✕</span>
      </div>

      {/* Coords */}
      <div style={{ padding: '10px 16px', backgroundColor: '#f7fafc', borderBottom: '1px solid #e2e8f0', fontSize: 12, color: '#718096' }}>
        {parseFloat(location.latitude).toFixed(5)}, {parseFloat(location.longitude).toFixed(5)}
      </div>

      {/* Actions */}
      <div style={{ padding: '10px 16px', borderBottom: '1px solid #e2e8f0', display: 'flex', gap: 8 }}>
        <button onClick={() => onEdit(location)} style={{ flex: 1, padding: '7px', backgroundColor: '#ebf8ff', border: '1px solid #bee3f8', borderRadius: 6, cursor: 'pointer', fontSize: 13, color: '#2b6cb0' }}>✏️ Edit</button>
        <button onClick={() => onDelete(location)} style={{ flex: 1, padding: '7px', backgroundColor: '#fff5f5', border: '1px solid #feb2b2', borderRadius: 6, cursor: 'pointer', fontSize: 13, color: '#c53030' }}>🗑️ Delete</button>
      </div>

      {/* Racks */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ fontWeight: 'bold', fontSize: 14 }}>Racks</div>
          <button onClick={() => setShowAddRack(o => !o)} style={{ fontSize: 12, padding: '4px 10px', backgroundColor: '#3182ce', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer' }}>+ Add Rack</button>
        </div>

        {showAddRack && (
          <div style={{ marginBottom: 12, padding: 10, backgroundColor: '#ebf8ff', borderRadius: 8, border: '1px solid #bee3f8' }}>
            <input
              autoFocus
              type="text"
              placeholder="Rack label (e.g. Rack A)"
              value={newRackLabel}
              onChange={e => setNewRackLabel(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addRack()}
              style={{ width: '100%', padding: '7px 10px', border: '1px solid #ccc', borderRadius: 6, fontSize: 13, boxSizing: 'border-box', marginBottom: 8 }}
            />
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setShowAddRack(false)} style={{ flex: 1, padding: '6px', backgroundColor: '#edf2f7', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>Cancel</button>
              <button onClick={addRack} style={{ flex: 1, padding: '6px', backgroundColor: '#3182ce', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 'bold' }}>Add</button>
            </div>
          </div>
        )}

        {loading && <div style={{ color: '#aaa', fontSize: 13, textAlign: 'center', padding: 20 }}>Loading...</div>}

        {!loading && racks.length === 0 && (
          <div style={{ color: '#aaa', fontSize: 13, textAlign: 'center', padding: 20 }}>No racks at this location yet.</div>
        )}

        {racks.map(rack => (
          <div key={rack.id} onClick={() => onOpenRack(rack)} style={{ padding: '12px 14px', marginBottom: 8, backgroundColor: '#f7fafc', border: '1px solid #e2e8f0', borderRadius: 8, cursor: 'pointer' }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#ebf8ff'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = '#f7fafc'}>
            <div style={{ fontWeight: 'bold', fontSize: 14 }}>{rack.label}</div>
            <div style={{ fontSize: 12, color: '#718096', marginTop: 3 }}>{rack.total_units}U · {rack.equipment_count || 0} items</div>
            {rack.room && <div style={{ fontSize: 12, color: '#a0aec0', marginTop: 2 }}>Room: {rack.room}</div>}
          </div>
        ))}

        {/* Safety Fields */}
        {safetyEdit && (
          <div style={{ marginTop: 18, marginBottom: 4, border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
            <div style={{ padding: '8px 12px', backgroundColor: '#fef9c3', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 700, fontSize: 13 }}>Site Safety</span>
              <button onClick={saveSafety} disabled={savingS} style={{ fontSize: 12, padding: '4px 10px', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: 5, cursor: 'pointer', fontWeight: 700 }}>
                {savingS ? '…' : 'Save'}
              </button>
            </div>
            <div style={{ padding: 10, display: 'grid', gap: 8 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                <input type="checkbox" checked={safetyEdit.high_risk_facility} onChange={e => setSafetyEdit(p => ({ ...p, high_risk_facility: e.target.checked }))} />
                <span style={{ color: safetyEdit.high_risk_facility ? '#b91c1c' : '#374151', fontWeight: safetyEdit.high_risk_facility ? 700 : 400 }}>High Risk Facility</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                <input type="checkbox" checked={safetyEdit.nerc_compliance} onChange={e => setSafetyEdit(p => ({ ...p, nerc_compliance: e.target.checked }))} />
                <span style={{ color: safetyEdit.nerc_compliance ? '#b45309' : '#374151', fontWeight: safetyEdit.nerc_compliance ? 700 : 400 }}>NERC Compliance Required</span>
              </label>
              <div>
                <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 700, textTransform: 'uppercase', marginBottom: 3 }}>Logged In To (default)</div>
                <select value={safetyEdit.logged_in_to_default} onChange={e => setSafetyEdit(p => ({ ...p, logged_in_to_default: e.target.value }))} style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}>
                  <option value="">— None —</option>
                  {LOGGED_IN_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 700, textTransform: 'uppercase', marginBottom: 3 }}>911 Directions</div>
                <textarea value={safetyEdit.directions_911} onChange={e => setSafetyEdit(p => ({ ...p, directions_911: e.target.value }))} rows={2} style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }} />
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 700, textTransform: 'uppercase', marginBottom: 3 }}>Nearest Hospital</div>
                <input value={safetyEdit.nearest_hospital} onChange={e => setSafetyEdit(p => ({ ...p, nearest_hospital: e.target.value }))} style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
              </div>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '18px 0 12px' }}>
          <div style={{ fontWeight: 'bold', fontSize: 14 }}>FMP History</div>
          <div style={{ fontSize: 11, color: '#94a3b8' }}>{fmps.length} total</div>
        </div>

        {fmps.length === 0 && (
          <div style={{ color: '#aaa', fontSize: 13, textAlign: 'center', padding: '8px 0 18px' }}>No FMPs for this site yet.</div>
        )}

        {fmps.map(fmp => {
          const s = statusStyles[fmp.status] || statusStyles.draft;
          return (
            <div key={fmp.id} onClick={() => setSelectedFmp(fmp)} style={{ padding: '10px 12px', marginBottom: 8, backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, cursor: 'pointer' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 5 }}>
                <div style={{ fontWeight: 'bold', fontSize: 13, color: '#2d3748' }}>{fmp.rack_label || 'Rack'} · {(fmp.fmp_type || 'rack_view').replace(/_/g, ' ')}</div>
                <span style={{ fontSize: 10, color: s.color, backgroundColor: s.bg, padding: '2px 6px', borderRadius: 4, whiteSpace: 'nowrap' }}>{(fmp.status || 'draft').replace(/_/g, ' ')}</span>
              </div>
              <div style={{ fontSize: 11, color: '#718096' }}>{new Date(fmp.generated_at).toLocaleDateString()} · {fmp.generated_by_name || 'Unknown'}</div>
            </div>
          );
        })}
      </div>

      <FMPPreviewModal fmp={selectedFmp} onClose={() => setSelectedFmp(null)} />
    </div>
  );
}
